/**
 * Permissões efetivas no frontend (paridade com useCompanyRestrictions do Mukta Admin).
 * Carrega /auth/me/permissions e replica a MESMA precedência do backend (auth/permissions.ts):
 *   kill-switch global > override do usuário > entitlement do tenant > restrição do perfil.
 * O front é conveniência (esconder o que o usuário não pode ver); o backend é a fronteira real.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetch, getAccessToken } from "./api";

export type Membership = { company_id: string; company_name: string | null; company_type: string | null; role: string; is_primary: boolean };
export type PermissionState = {
  isSuper: boolean;
  companyId: string | null;
  role: string;
  allowedRoles: string[] | null;
  forceOwnOnly: boolean;
  entitlementsDenied: string[];
  roleHidden: string[];
  roleDisabledCapabilities: string[];
  overrideFeatures: Record<string, "allow" | "deny">;
  overrideCapabilities: Record<string, "allow" | "deny">;
  globallyOff: string[];
  memberships: Membership[];
};

export function featureAllowed(s: PermissionState | null, id: string): boolean {
  if (!s) return true; // ainda carregando → não esconde (evita flicker); o backend barra de fato
  if (s.globallyOff.includes(id)) return false;
  if (s.isSuper) return true;
  if (s.overrideFeatures[id] === "allow") return true;
  if (s.overrideFeatures[id] === "deny") return false;
  if (s.entitlementsDenied.includes(id)) return false;
  if (s.roleHidden.includes(id)) return false;
  return true;
}
export function capabilityAllowed(s: PermissionState | null, id: string): boolean {
  if (!s) return true;
  if (s.isSuper) return true;
  if (s.overrideCapabilities[id] === "allow") return true;
  if (s.overrideCapabilities[id] === "deny") return false;
  if (s.roleDisabledCapabilities.includes(id)) return false;
  return true;
}

type Ctx = {
  perms: PermissionState | null;
  loading: boolean;
  isFeatureAllowed: (id: string) => boolean;
  isCapabilityAllowed: (id: string) => boolean;
  reload: () => void;
};
const PermissionsContext = createContext<Ctx | null>(null);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [perms, setPerms] = useState<PermissionState | null>(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    if (!getAccessToken()) { setPerms(null); setLoading(false); return; }
    setLoading(true);
    apiFetch<PermissionState>("/auth/me/permissions")
      .then(setPerms)
      .catch(() => setPerms(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <PermissionsContext.Provider
      value={{
        perms, loading,
        isFeatureAllowed: (id) => featureAllowed(perms, id),
        isCapabilityAllowed: (id) => capabilityAllowed(perms, id),
        reload,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): Ctx {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error("usePermissions must be used inside PermissionsProvider");
  return ctx;
}
